// Settlement layout builders — deterministic per-city building placement
// (houses, path centerpieces, fortifications). Split out of Settlements.tsx;
// geometry/palettes live in settlementForms.ts, React pieces in
// SettlementPieces.tsx.

import type { SceneNode } from '@aod/protocol';
import { playerColor } from '@aod/ui';
import * as THREE from 'three';
import { hexNoise } from './elevation.js';
import { CULTURE_ROOFS, CULTURE_WALLS, cultureForm, cultureOf } from './settlementCultures.js';
import {
  type Archetype,
  type BuildingForm,
  type BuildingStyle,
  PALISADE_TIP,
  PALISADE_WOOD,
  PATH_PALETTES,
  ROOF_PALETTES,
  RUBBLE,
  RUIN_WALL,
  WALL_PALETTES,
  WALL_RADIUS,
  pathPalette,
} from './settlementForms.js';

// --- Layout ---

export interface SettlementBuilding {
  readonly cityId: string;
  readonly form: BuildingForm;
  readonly x: number;
  readonly y: number;
  readonly z: number;
  readonly rotY: number;
  /** Tilt around Z (rare — tilted path props: arcane monolith, chaos lean). */
  readonly rotZ?: number;
  readonly scaleXZ: number;
  readonly scaleY: number;
  /** Linear-space RGB. */
  readonly wallColor: readonly [number, number, number];
  readonly roofColor: readonly [number, number, number];
}

const HOUSES_BY_TIER = [0, 4, 6, 7, 8, 9];
/** Overall building scale — sized against the scatter trees (~0.8 tall). */
const SETTLEMENT_SCALE = 1.4;
const CLEAR_CENTRE = 0.38; // the city-centre prefab and HP bar sit here
const OUTER = 0.78;
const MIN_GAP = 0.37;

const _c = new THREE.Color();
const _hsl = { h: 0, s: 0, l: 0 };

export function pick<T>(list: readonly T[], h: number): T {
  return list[Math.min(list.length - 1, Math.floor(h * list.length))]!;
}

/** Palette pick + weathering jitter (lightness / slight hue), as linear RGB. */
export function weathered(
  palette: readonly string[],
  h1: number,
  h2: number,
): [number, number, number] {
  _c.set(pick(palette, h1)).getHSL(_hsl);
  _c.setHSL(_hsl.h + (h2 - 0.5) * 0.02, _hsl.s * (0.85 + h2 * 0.2), _hsl.l * (0.9 + h1 * 0.18));
  return _c.toArray() as [number, number, number];
}

export function inHex(dx: number, dz: number, r: number): boolean {
  const q2 = ((Math.sqrt(3) / 3) * dx - (1 / 3) * dz) / r;
  const r2 = ((2 / 3) * dz) / r;
  return Math.max(Math.abs(q2), Math.abs(r2), Math.abs(q2 + r2)) <= 1;
}

/**
 * Deterministic building layout for one city. Pure: same city props in,
 * same buildings out. `baseY` is the city tile's surface height.
 */
export function buildSettlement(city: SceneNode, baseY: number): SettlementBuilding[] {
  const props = (city.props ?? {}) as Record<string, unknown>;
  const q = (props.q as number | undefined) ?? 0;
  const r = (props.r as number | undefined) ?? 0;
  const tier = Math.max(1, Math.min(5, (props.tier as number | undefined) ?? 1));
  const isCapital = props.isCapital === true;
  const hp = (props.hp as number | undefined) ?? 1;
  const hpMax = (props.hpMax as number | undefined) ?? 1;
  const damage = hpMax > 0 ? Math.max(0, Math.min(1, 1 - hp / hpMax)) : 0;
  const archetype = props.archetype as Archetype | undefined;
  const alignment = props.alignment as string | undefined;
  // Player type: an owned city follows its culture; unowned / unknown keep the per-hex mix.
  const culture = props.ownerId === undefined ? undefined : cultureOf(props.culture);
  const [cx, , cz] = city.transform.position;

  // City-level culture: one dominant style, the others as a minority.
  const styles: BuildingStyle[] = ['timber', 'stone', 'brick'];
  const dominant = pick(styles, hexNoise(q * 1.7 + 0.3, r * 2.3 - 0.9));
  const ownerColor = new THREE.Color(playerColor(props.ownerId as number | undefined));
  // Dark-alignment tone on ordinary houses: cooled, desaturated, darker
  // (spec §2.2) — the near-black constructions must read in fog, not mush.
  const dark = alignment === 'dark';

  const out: SettlementBuilding[] = [];
  const placed: Array<[number, number]> = [];

  if (isCapital || tier >= 3) {
    // Keep beside the centre prefab, roofed in the owner's colour.
    const kx = cx + 0.44;
    const kz = cz - 0.28;
    placed.push([kx - cx, kz - cz]);
    out.push({
      cityId: city.id,
      form: damage > 0.6 ? 'ruin' : cultureForm('keep', culture),
      x: kx,
      y: baseY,
      z: kz,
      rotY: hexNoise(q, r) * Math.PI * 2,
      scaleXZ: SETTLEMENT_SCALE,
      scaleY: SETTLEMENT_SCALE * (1 + tier * 0.08),
      wallColor: weathered(
        culture ? CULTURE_WALLS[culture] : WALL_PALETTES.stone,
        hexNoise(q + 5, r),
        hexNoise(q, r + 5),
      ),
      roofColor: ownerColor.toArray() as [number, number, number],
    });
  }

  const target = HOUSES_BY_TIER[tier] ?? 4;
  for (
    let attempt = 0;
    out.length < target + (isCapital || tier >= 3 ? 1 : 0) && attempt < 90;
    attempt++
  ) {
    const ha = hexNoise(q * 3.1 + attempt * 0.71, r * 2.7 - attempt * 0.37);
    const hr = hexNoise(r * 1.9 - attempt * 0.53, q * 4.3 + attempt * 0.29);
    const angle = ha * Math.PI * 2;
    const radius = CLEAR_CENTRE + hr * (OUTER - CLEAR_CENTRE);
    const dx = Math.cos(angle) * radius;
    const dz = Math.sin(angle) * radius;
    // Inside the wall line (edge distance WALL_RADIUS * √3/2 ≈ 0.8).
    if (!inHex(dx, dz, 0.8)) continue;
    if (placed.some(([px, pz]) => Math.hypot(px - dx, pz - dz) < MIN_GAP)) continue;
    placed.push([dx, dz]);

    const h1 = hexNoise(dx * 13.1 + q, dz * 11.7 + r);
    const h2 = hexNoise(dz * 9.3 - q, dx * 7.9 - r);
    const h3 = hexNoise(dx * 5.3 + r, dz * 3.1 + q);

    // 70% dominant culture, the rest split between the other two.
    const style: BuildingStyle =
      h1 < 0.7
        ? dominant
        : pick(
            styles.filter((s) => s !== dominant),
            (h1 - 0.7) / 0.3,
          );
    const ruined = h2 < damage * 0.85;
    const wallPalette = culture ? CULTURE_WALLS[culture] : WALL_PALETTES[style];
    const roofPalette = culture ? CULTURE_ROOFS[culture] : ROOF_PALETTES[style];
    const form: BuildingForm = ruined
      ? 'ruin'
      : tier >= 2 && h3 > 0.72
        ? 'townhouse'
        : h3 > 0.45
          ? 'longhouse'
          : 'cottage';

    out.push({
      cityId: city.id,
      form: cultureForm(form, culture),
      x: cx + dx,
      y: baseY,
      z: cz + dz,
      // Face the centre (ridge tangent to the ring) with a little slop.
      rotY: -angle + (h2 - 0.5) * 0.6,
      scaleXZ: SETTLEMENT_SCALE * (0.88 + h3 * 0.24),
      scaleY: SETTLEMENT_SCALE * (0.9 + h1 * 0.25),
      wallColor: dark
        ? toneDark(weathered(ruined ? RUIN_WALL : wallPalette, h2, h3))
        : weathered(ruined ? RUIN_WALL : wallPalette, h2, h3),
      roofColor: dark
        ? toneDark(weathered(ruined ? RUBBLE : roofPalette, h3, h1))
        : weathered(ruined ? RUBBLE : roofPalette, h3, h1),
    });
  }

  out.push(...buildPathProps(city, baseY, archetype, alignment));
  out.push(...buildFortifications(city, baseY, damage));
  return out;
}

/** Dark-tone a linear RGB triple: darker, desaturated, cooled. */
export function toneDark(rgb: readonly [number, number, number]): [number, number, number] {
  return [rgb[0] * 0.42 + 0.02, rgb[1] * 0.42 + 0.02, rgb[2] * 0.46 + 0.035];
}

/**
 * The dominion-path centerpiece: one silhouette prop per doctrine archetype
 * (spec §2.1) in the reserved centre ring, scaling gently with tier — two
 * props from tier 3. Omitted when the city has no archetype (human seats
 * without an alignment). Pure, like the rest of the layout.
 */
export function buildPathProps(
  city: SceneNode,
  baseY: number,
  archetype: Archetype | undefined,
  alignment: string | undefined,
): SettlementBuilding[] {
  if (!archetype || !(archetype in PATH_PALETTES)) return [];
  const props = (city.props ?? {}) as Record<string, unknown>;
  const q = (props.q as number | undefined) ?? 0;
  const r = (props.r as number | undefined) ?? 0;
  const tier = Math.max(1, Math.min(5, (props.tier as number | undefined) ?? 1));
  const [cx, , cz] = city.transform.position;
  const palette = pathPalette(archetype, alignment);
  const count = tier >= 3 ? 2 : 1;

  const out: SettlementBuilding[] = [];
  for (let i = 0; i < count; i++) {
    // Deterministic spot in the centre ring, clear of the centre prefab.
    const angle = hexNoise(q * 4.7 + i * 3.1 + 11, r * 3.9 - i * 1.7 - 7) * Math.PI * 2;
    const radius = 0.22 + i * 0.06;
    const x = cx + Math.cos(angle) * radius;
    const z = cz + Math.sin(angle) * radius;
    const h = hexNoise(q + i * 5.3, r - i * 2.9);
    const form: BuildingForm =
      `path${archetype[0]!.toUpperCase()}${archetype.slice(1)}` as BuildingForm;
    const rotY = h * Math.PI * 2;
    // Arcane monoliths lean; chaos props lean harder (spec §2.3 Chaos).
    const lean =
      archetype === 'arcane' ? 0.16 + h * 0.1 : archetype === 'chaos' ? 0.22 + h * 0.16 : 0;
    out.push({
      cityId: city.id,
      form,
      x,
      y: baseY,
      z,
      rotY,
      rotZ: lean === 0 ? undefined : lean * (h > 0.5 ? 1 : -1),
      scaleXZ: SETTLEMENT_SCALE,
      scaleY: SETTLEMENT_SCALE * (0.9 + tier * 0.07),
      wallColor: weathered(palette.walls, h, hexNoise(r + i, q - i)),
      roofColor: weathered(palette.roofs, hexNoise(q * 2 + i, r * 2 - i), h),
    });
  }
  return out;
}

/** Highest completed level of a building chain (`walls_1`..`walls_3`), or 0. */
function chainLevel(completed: readonly string[], prefix: string): number {
  let level = 0;
  for (const id of completed) {
    const m = id.startsWith(`${prefix}_`) ? Number(id.slice(prefix.length + 1)) : Number.NaN;
    if (Number.isInteger(m) && m > level) level = m;
  }
  return level;
}

/** Corner towers per tower level: flank the gate, then the far side, then all six. */
const TOWER_CORNERS: readonly (readonly number[])[] = [
  [],
  [0, 1],
  [0, 1, 3, 4],
  [0, 1, 2, 3, 4, 5],
];

/**
 * Walls along the hex edges (gate: the middle third of edge 0) and towers on
 * the corners, from the city's completed `walls_n` / `tower_n`. Damage opens
 * breaches — a share of wall segments is dropped.
 */
export function buildFortifications(
  city: SceneNode,
  baseY: number,
  damage: number,
): SettlementBuilding[] {
  const props = (city.props ?? {}) as Record<string, unknown>;
  const completed = (props.completedBuildings as string[] | undefined) ?? [];
  const walls = Math.min(3, chainLevel(completed, 'walls'));
  const towers = Math.min(3, chainLevel(completed, 'tower'));
  if (walls === 0 && towers === 0) return [];

  const q = (props.q as number | undefined) ?? 0;
  const r = (props.r as number | undefined) ?? 0;
  const [cx, , cz] = city.transform.position;
  const corner = (i: number): [number, number] => {
    const a = ((30 + 60 * i) * Math.PI) / 180;
    return [Math.cos(a) * WALL_RADIUS, Math.sin(a) * WALL_RADIUS];
  };
  const out: SettlementBuilding[] = [];

  if (walls > 0) {
    const form: BuildingForm = walls >= 3 ? 'highWall' : walls === 2 ? 'wall' : 'palisade';
    for (let e = 0; e < 6; e++) {
      const [ax, az] = corner(e);
      const [bx, bz] = corner(e + 1);
      // Align local +X with the edge direction.
      const rotY = Math.atan2(-(bz - az), bx - ax);
      for (let third = 0; third < 3; third++) {
        if (e === 0 && third === 1) continue; // gate
        const t = (third + 0.5) / 3;
        const h1 = hexNoise(q * 7.1 + e * 1.3 + third, r * 5.3 - e * 0.7);
        if (h1 < damage * 0.6) continue; // breach
        out.push({
          cityId: city.id,
          form,
          x: cx + ax + (bx - ax) * t,
          y: baseY,
          z: cz + az + (bz - az) * t,
          rotY,
          scaleXZ: 1,
          scaleY: 0.94 + hexNoise(e + 0.5 * third, q - r) * 0.12,
          wallColor: weathered(
            form === 'palisade' ? PALISADE_WOOD : WALL_PALETTES.stone,
            h1,
            hexNoise(r + e, q + third),
          ),
          roofColor: weathered(
            form === 'palisade' ? PALISADE_TIP : WALL_PALETTES.stone,
            hexNoise(q - e, r + third),
            h1,
          ),
        });
      }
    }
  }

  for (const i of TOWER_CORNERS[towers] ?? []) {
    const [tx, tz] = corner(i);
    const h = hexNoise(q * 3.3 + i, r * 2.1 - i);
    out.push({
      cityId: city.id,
      form: 'tower',
      x: cx + tx,
      y: baseY,
      z: cz + tz,
      rotY: h * Math.PI,
      scaleXZ: 1,
      scaleY: 0.85 + towers * 0.2,
      wallColor: weathered(WALL_PALETTES.stone, h, hexNoise(i, q)),
      roofColor: weathered(ROOF_PALETTES.stone, hexNoise(r, i), h),
    });
  }
  return out;
}
